/**
 * Month grid built from current task, schedule, and calendar stores. Memoizes merged blocks,
 * changes its displayed month locally, and navigates a selected date into the day view; does not
 * fetch a new calendar range itself.
 *
 * @module apps/web/src/components/calendar/MonthView
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTasksStore } from '@/stores/tasksStore';
import type { ScheduleBlock } from '@fluxability/types';
import { useScheduleStore } from '@/stores/scheduleStore';
import { CalendarChrome } from './CalendarChrome';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * Month grid built from current task, schedule, and calendar stores. Memoizes merged blocks,
 * changes its displayed month locally, and navigates a selected date into the day view; does not
 * fetch a new calendar range itself.
 */
export function MonthView() {
  const [depth, setDepth] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const navigate = useNavigate();
  const blocks = useScheduleStore(state => state.blocks);
  const calendarEvents = useScheduleStore(state => state.calendarEvents);
  const setCurrentDate = useScheduleStore(state => state.setCurrentDate);
  const tasks = useTasksStore(state => state.tasks);
  const visibleBlocks = useMemo(() => {
    const combined = new Map<string, ScheduleBlock>(blocks.map(block => [block.id, block]));
    for (const task of tasks)
      if (task.scheduledStart && task.scheduledEnd)
        combined.set(`task-${task.id}`, {
          id: `task-${task.id}`,
          taskId: task.id,
          title: task.title,
          startTime: task.scheduledStart,
          endTime: task.scheduledEnd,
          isGhost: false,
          priority: task.priority,
        });
    for (const event of calendarEvents)
      combined.set(`gcal-${event.id}`, {
        id: `gcal-${event.id}`,
        taskId: event.id,
        title: event.title,
        startTime: event.start,
        endTime: event.end,
        isGhost: false,
        isCalendarEvent: true,
        allowsConflict: event.allowConflict,
      });
    return [...combined.values()];
  }, [blocks, tasks, calendarEvents]);

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // First day of month
    const firstDay = new Date(year, month, 1);
    const startingDayOfWeek = firstDay.getDay();

    // Last day of month
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Days from previous month
    const prevMonthDays = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    const prevMonth = new Date(year, month, 0);
    const daysInPrevMonth = prevMonth.getDate();

    const days: Date[] = [];

    // Previous month days
    for (let i = prevMonthDays; i > 0; i--) {
      days.push(new Date(year, month - 1, daysInPrevMonth - i + 1));
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    // Next month days to fill the grid
    const remainingDays = 42 - days.length; // 6 weeks
    for (let i = 1; i <= remainingDays; i++) {
      days.push(new Date(year, month + 1, i));
    }

    return days;
  }, [currentMonth]);

  // Helper to format a Date as YYYY-MM-DD in local time
  const toLocalDateString = (d: Date) => {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Group blocks by day
  const blocksByDay = useMemo(() => {
    const grouped: Record<string, typeof blocks> = {};
    visibleBlocks.forEach(block => {
      const dateKey = toLocalDateString(new Date(block.startTime));
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(block);
    });
    return grouped;
  }, [visibleBlocks]);

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    const now = new Date();
    setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  const monthName = currentMonth.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="activity-calendar" data-depth={depth}>
      <CalendarChrome
        view="Month"
        period={monthName}
        previous={goToPreviousMonth}
        next={goToNextMonth}
        today={goToToday}
        depth={depth}
        setDepth={setDepth}
      />

      {/* Calendar grid */}
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="calendar-month-grid grid h-full grid-cols-7">
          {/* Day headers */}
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <div
              key={day}
              className="text-muted-foreground py-1 text-center text-xs font-medium sm:py-2 sm:text-sm"
            >
              {day}
            </div>
          ))}

          {/* Calendar days */}
          {calendarDays.map((day, idx) => {
            const dateKey = toLocalDateString(day);
            const dayBlocks = blocksByDay[dateKey] || [];
            const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
            const isToday = day.toDateString() === new Date().toDateString();

            return (
              <button
                type="button"
                aria-label={`View ${day.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}`}
                onClick={() => {
                  setCurrentDate(day);
                  navigate('/day');
                }}
                key={idx}
                className={cn(
                  'calendar-month-cell text-left text-xs sm:text-sm',
                  !isCurrentMonth && 'opacity-40',
                  isToday && 'ring-primary ring-2'
                )}
              >
                <div className={cn('mb-1 font-medium sm:mb-2', isToday && 'text-primary')}>
                  {day.getDate()}
                </div>
                <div className="space-y-0.5 sm:space-y-1">
                  {dayBlocks.slice(0, 2).map(block => (
                    <div
                      key={block.id}
                      className={cn(
                        'bg-primary/10 truncate rounded p-0.5 text-[10px] sm:p-1 sm:text-xs',
                        block.allowsConflict && 'border border-dashed border-blue-400'
                      )}
                      title={
                        block.allowsConflict ? `${block.title} (activities may overlap)` : undefined
                      }
                    >
                      {block.title}
                    </div>
                  ))}
                  {dayBlocks.length > 2 && (
                    <Badge variant="secondary" className="text-[9px] sm:text-xs">
                      +{dayBlocks.length - 2}
                    </Badge>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
